import { INVENTORY_MODULE } from "@/api_framework/api_config";
import { useConfiguration } from "@/app/hooks/useConfiguration";
import axios, { AxiosError } from "axios";
import { useCallback } from "react";
import AxiosCurlirize from "axios-curlirize";
import { useCurlLogger } from "@/app/hooks/useCurlLogger";
import { useAPIUtils } from "@/app/hooks/useAPIUtils";

AxiosCurlirize(axios, () => {});

export interface IFinanceConfigChangeListPayload {
  status?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  page_size?: number | null;
  page_number?: number | null;
  sku_name?: string | null;
}

interface IFinanceConfigChangeListResponse<TData = unknown> {
  status: boolean;
  message?: string;
  data?:
    | TData[]
    | {
        total_items?: number;
        page_number?: number;
        page_size?: number;
        data?: TData[];
      };
}

export const useFinanceConfigChangeListAPI = () => {
  const configuration = useConfiguration();
  const { captureError } = useAPIUtils();
  const { sendCurl } = useCurlLogger();
  const getAuthHeaders = useCallback(() => {
    const accessToken = localStorage.getItem(configuration.localStorage.ACCESS_TOKEN.key);
    return {
      Authorization: `${accessToken}`,
      "Content-Type": "application/json",
    };
  }, [configuration.localStorage.ACCESS_TOKEN.key]);

  const execute = useCallback(
    async <TData = unknown>(payload: IFinanceConfigChangeListPayload) => {
      try {
        const baseURL = INVENTORY_MODULE?.FINANCE_CONFIG_CHANGE_ACTION?.baseURL ?? "";
        const response = await axios.post<IFinanceConfigChangeListResponse<TData>>(
          baseURL,
          {
            status: payload.status ?? null,
            start_date: payload.start_date ?? null,
            end_date: payload.end_date ?? null,
            page_size: payload.page_size ?? null,
            page_number: payload.page_number ?? null,
            sku_name: payload.sku_name ?? null,
          },
          {
            headers: getAuthHeaders(),
          },
        );

        return {
          status: response.data?.status === true,
          message: response.data?.message ?? "",
          data: Array.isArray(response.data?.data)
            ? response.data.data
            : Array.isArray(response.data?.data?.data)
              ? response.data.data.data
              : [],
        };
      } catch (e: unknown) {
        const error = e as AxiosError;
        sendCurl(error?.config?.["curlCommand"]);
        captureError(error);
        return {
          status: false,
          message: error?.message ?? "Failed to fetch request list",
          data: [] as TData[],
        };
      }
    },
    [captureError, getAuthHeaders, sendCurl],
  );

  const exportCSV = useCallback(async () => {
    try {
      const baseURL = INVENTORY_MODULE?.FINANCE_CONFIG_CHANGE_ACTION?.baseURL ?? "";
      const response = await axios.post(
        `${baseURL}?convert_to_csv=1`,
        {},
        {
          headers: getAuthHeaders(),
          responseType: "blob",
        },
      );

      const blob = new Blob([response.data], { type: "text/csv;charset=utf-8;" });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = "finance_sku_approval.csv";
      link.click();
      window.URL.revokeObjectURL(downloadUrl);

      return { status: true, message: "" };
    } catch (e: unknown) {
      const error = e as AxiosError;
      sendCurl(error?.config?.["curlCommand"]);
      captureError(error);
      return {
        status: false,
        message: error?.message ?? "Failed to export CSV",
      };
    }
  }, [captureError, getAuthHeaders, sendCurl]);

  return { execute, exportCSV };
};
